import { MatchCompletedEnum } from '@/domain/constants/constants';
import { Matches, SubMatch } from '@/entities';
import { SequenceStatus, TieBreakerSequence } from '@/entities/rotations/GameSequence';
import { Rotation, Sequence } from '@/entities/rotations/LastSequence';
import { RotationPlayerInfo } from '@/entities/rotations/RotationsInfo';
import { LineupEntryOrder } from '@/infrastructure/network/dto/SeasonDivisionPool';
import { CreateGameSequencesPayload, GameSequenceItem } from '@/services/actions/tie-breaker/rotations';
import { TieBreakerPair } from '@/utils/types';

export enum PlayerGender {
	MENS = 'M',
	WOMENS = 'F'
}

export const isValidScore = (val: string): boolean => {
	if (!/^\d{0,2}$/.test(val)) return false;
	const num = Number(val);
	return val === '' || (num >= 0 && num <= 99);
};

export const isValidMatchResult = (scoreOne: number | '', scoreTwo: number | '', winBy: number): boolean => {
	if (typeof scoreOne !== 'number' || typeof scoreTwo !== 'number') return false;
	const diff = Math.abs(scoreOne - scoreTwo);
	return diff >= winBy;
};

const groupGenderMap: Record<string, string> = {
	Mens: 'M',
	Womens: 'F',
	Mixed: 'MF',
	Coed: 'MF'
};

export function isPlayerDisabled(gender: PlayerGender, groupTitle: string): boolean {
	const allowedGenders = groupGenderMap[groupTitle];
	if (!allowedGenders) return false;
	return !allowedGenders.includes(gender);
}

export const getLegitMatches = (matches: SubMatch[]): SubMatch[] => {
	return matches.filter((match) => {
		const isNotTieBreaker = match.isTieBreaker !== true;
		const isDifferentCompletedType = match.matchCompletedType !== 4 && match.matchCompletedType !== 6;
		return isNotTieBreaker && isDifferentCompletedType;
	});
};

export const areAllLegitMatchesPlayed = (matches: SubMatch[]): boolean => {
	const legitMatches = matches.filter((match) => match.isTieBreaker !== true);

	const notCompleted = legitMatches.filter(
		(match) =>
			match.matchStatus !== MatchCompletedEnum.WITHDRAWAL_MATCH_COMPLETED_TYPE ||
			![MatchCompletedEnum.PLAYED_MATCH_COMPLETED_TYPE, MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE].includes(
				match.matchCompletedType ?? -1
			)
	);

	return notCompleted.length === 0;
};

export const isMatchTied = (matchupData: Matches): boolean => {
	if (!matchupData) return false;

	const { winner, teamOneScore, teamTwoScore } = matchupData;

	return winner === 0 && typeof teamOneScore === 'number' && typeof teamTwoScore === 'number' && teamOneScore === teamTwoScore && teamOneScore > 0;
};

export const hasTieBreaker = (matches: SubMatch[]): boolean => {
	return matches.some((match) => match.isTieBreaker === true);
};

export const hasUnplayedTieBreaker = (matches: SubMatch[]): boolean => {
	return matches.some((match) => match.isTieBreaker === true && match.matchCompleted === null);
};

export const getLastActiveSequence = (sequences: TieBreakerSequence[]) =>
	[...sequences].reverse().find((seq) => seq.sequenceStatus === SequenceStatus.PLAYED || seq.sequenceStatus === SequenceStatus.IN_PROGRESS);

export type MoveDirection = 'up' | 'down';

export function reorderArrayItem<T>(array: T[], index: number, direction: MoveDirection): T[] {
	const targetIndex = direction === 'up' ? index - 1 : index + 1;
	if (targetIndex < 0 || targetIndex >= array.length) return array;

	const updated = [...array];
	const temp = updated[index]!;
	updated[index] = updated[targetIndex]!;
	updated[targetIndex] = temp;

	return updated;
}

type PlayerBasic = {
	uuid: string;
	fullName: string;
};

export type PlayedMatch = {
	uuid: string;
	left: PlayerBasic;
	right: PlayerBasic;
	leftScore: number;
	rightScore: number;
};

export const mapPlayedSequencesToMatches = (sequences: TieBreakerSequence[]): PlayedMatch[] => {
	return sequences
		.filter((s) => s.sequenceStatus === SequenceStatus.PLAYED)
		.map((s) => {
			if (!s.teamOnePlayerOneName || !s.teamTwoPlayerOneName) return null;

			return {
				uuid: s.uuid,
				left: {
					uuid: s.teamTwoPlayerOneUuid,
					fullName: s.teamTwoPlayerOneName,
					secondPlayerName: s.teamTwoPlayerTwoName ?? undefined
				},
				right: {
					uuid: s.teamOnePlayerOneUuid,
					fullName: s.teamOnePlayerOneName,
					secondPlayerName: s.teamOnePlayerTwoName ?? undefined
				},
				leftScore: s.teamTwoScore ?? 0,
				rightScore: s.teamOneScore ?? 0
			};
		})
		.filter(Boolean) as PlayedMatch[];
};

interface TotalScore {
	teamOneTotal: number;
	teamTwoTotal: number;
}

export const getTotalScoresFromSequences = (sequences: TieBreakerSequence[]): TotalScore => {
	return sequences.reduce(
		(acc, seq) => {
			return {
				teamOneTotal: acc.teamOneTotal + (seq.teamOneScore ?? 0),
				teamTwoTotal: acc.teamTwoTotal + (seq.teamTwoScore ?? 0)
			};
		},
		{ teamOneTotal: 0, teamTwoTotal: 0 }
	);
};

export const getWinner = (teamOneTotal: number, teamTwoTotal: number, game1Max: number, game1WinBy: number): 'teamOne' | 'teamTwo' | null => {
	const teamOneWon = teamOneTotal >= game1Max && teamOneTotal - teamTwoTotal >= game1WinBy;
	const teamTwoWon = teamTwoTotal >= game1Max && teamTwoTotal - teamOneTotal >= game1WinBy;

	if (teamOneWon) return 'teamOne';
	if (teamTwoWon) return 'teamTwo';

	return null;
};

type GetNextUpParams = {
	team: Rotation[];
	currentPlayerUuid: string;
	rotateOut: boolean;
	format: 'SINGLES' | 'DOUBLES';
};

export function getNextUpTeam({ team, currentPlayerUuid, rotateOut, format }: GetNextUpParams): Rotation[] {
	let result: Rotation[];

	if (!rotateOut) {
		result = team.filter((p) => p.playerOneUuid === currentPlayerUuid);
	} else {
		result = team.filter((p) => p.playerOneUuid !== currentPlayerUuid);
	}

	if (format === 'SINGLES' && result.length === 0) {
		const current = team.find((p) => p.playerOneUuid === currentPlayerUuid);
		if (current) {
			result = [current];
		}
	}

	if (format === 'DOUBLES' && result.length === 0) {
		const current = team.find((p) => p.playerOneUuid === currentPlayerUuid);
		if (current?.playerTwoUuid) {
			result = [current];
		}
	}

	return result;
}

export const getTieBreakerMatch = (matches: SubMatch[]): SubMatch | undefined => {
	return matches.find((match) => match.isTieBreaker === true);
};

export const getNextInLineup = (arr: RotationPlayerInfo[]) =>
	(arr.length >= 2 ? arr.slice(0, -1) : arr).map((r) => ({
		playerOneName: r.playerOneName,
		playerTwoName: r.playerTwoName ?? undefined,
		rotationUuid: r.rotationUuid
	}));

export function getNewDoublesPairs(data: (string | TieBreakerPair)[], existingPairKeys: string[]): TieBreakerPair[] {
	return data.filter((pair): pair is TieBreakerPair => {
		if (typeof pair !== 'object' || pair === null) return false;

		const key = [pair.player1, pair.player2].filter(Boolean).sort().join('-');
		return !existingPairKeys.includes(key);
	});
}
export function getNewSinglesPlayers(data: (string | TieBreakerPair)[], existingUuids: string[]): string[] {
	return data.filter((item): item is string => typeof item === 'string' && !existingUuids.includes(item));
}

export const extractTieBreakerInfo = (matchupData?: Matches) => {
	const match = matchupData?.matches.find((m) => m.isTieBreaker);

	return {
		match,
		id: match?.matchUuid || '',
		status: match?.matchStatus || 0,
		tieBreakerTitle: match?.roundText || 'TieBreaker',
		matchCompletedType: match?.matchCompletedType
	};
};

interface BuildFullPayloadParams {
	gameSequences: GameSequenceItem[];
	lastSequence: Sequence;
	teamOneNextUp: Rotation[];
	teamTwoNextUp: Rotation[];
	isDoubles: boolean;
	isEditMode?: boolean;
	editedScores?: {
		uuid: string;
		teamOneScore: number;
		teamTwoScore: number;
	}[];
}

export const buildSequencePayloadForSave = ({
	gameSequences,
	lastSequence,
	teamOneNextUp,
	teamTwoNextUp,
	isDoubles,
	isEditMode = false,
	editedScores
}: BuildFullPayloadParams): CreateGameSequencesPayload => {
	const baseMatchUuid = lastSequence.matchUuid;
	const gameNumber = lastSequence.gameNumber;
	const currentNumber = lastSequence.sequenceNumber;

	const updatedSequences = gameSequences.map((seq) => {
		const edited = editedScores?.find((e) => e.uuid === seq.uuid);

		if (!edited) return seq;

		const isScoreChanged = edited.teamOneScore !== seq.teamOneScore || edited.teamTwoScore !== seq.teamTwoScore;

		return {
			...seq,
			teamOneScore: edited.teamOneScore,
			teamTwoScore: edited.teamTwoScore,
			sequenceStatus: isScoreChanged || seq.sequenceStatus !== SequenceStatus.PLAYED ? SequenceStatus.PLAYED : seq.sequenceStatus,
			teamOnePlayerTwoUuid: seq.teamOnePlayerTwoUuid ?? undefined,
			teamTwoPlayerTwoUuid: seq.teamTwoPlayerTwoUuid ?? undefined
		};
	});

	if (isEditMode) {
		return {
			sequences: updatedSequences
		};
	}

	const newSequence: GameSequenceItem = {
		matchUuid: baseMatchUuid,
		sequenceNumber: currentNumber + 1,
		gameNumber,
		teamOneScore: 0,
		teamTwoScore: 0,
		sequenceStatus: SequenceStatus.NOT_PLAYED,
		teamOnePlayerOneUuid: teamOneNextUp[0]?.playerOneUuid ?? '',
		teamTwoPlayerOneUuid: teamTwoNextUp[0]?.playerOneUuid ?? ''
	};

	if (isDoubles) {
		newSequence.teamOnePlayerTwoUuid = teamOneNextUp[0]?.playerTwoUuid ?? '';
		newSequence.teamTwoPlayerTwoUuid = teamTwoNextUp[0]?.playerTwoUuid ?? '';
	}

	return {
		sequences: [...updatedSequences, newSequence]
	};
};

interface ShouldShowTieBreakerParams {
	permissionsStore: { gamedayAccess: boolean };
	isAwayTeamCaptain: boolean;
	isHomeTeamCaptain: boolean;
	allAwayPlayersSelected: boolean;
	allHomePlayersSelected: boolean;
	hasTeamOneTieBreakerLineup: boolean;
	hasTeamTwoTieBreakerLineup: boolean;
	poolData?: {
		lineupEntryOrder?: LineupEntryOrder;
	};
}

export const shouldShowTieBreakerButton = ({
	permissionsStore,
	isAwayTeamCaptain,
	isHomeTeamCaptain,
	allAwayPlayersSelected,
	allHomePlayersSelected,
	hasTeamOneTieBreakerLineup,
	hasTeamTwoTieBreakerLineup,
	poolData
}: ShouldShowTieBreakerParams): boolean => {
	if (permissionsStore.gamedayAccess) return true;

	if (isAwayTeamCaptain && allAwayPlayersSelected && !hasTeamTwoTieBreakerLineup) {
		if (
			poolData?.lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY ||
			poolData?.lineupEntryOrder === LineupEntryOrder.EITHER_TEAM_FIRST_LINEUP_ENTRY
		) {
			return true;
		}

		if (poolData?.lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY && hasTeamOneTieBreakerLineup) {
			return true;
		}
	}

	if (isHomeTeamCaptain && allHomePlayersSelected && !hasTeamOneTieBreakerLineup) {
		if (
			poolData?.lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY ||
			poolData?.lineupEntryOrder === LineupEntryOrder.EITHER_TEAM_FIRST_LINEUP_ENTRY
		) {
			return true;
		}

		if (poolData?.lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY && hasTeamTwoTieBreakerLineup) {
			return true;
		}
	}

	return false;
};
